import { Button, makeStyles, Menu, MenuItem, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import React, { useState } from 'react'

export interface OptionsConsumerToggleProps {
  value: 'crew' | 'customer'
  onChange: (value: 'crew' | 'customer') => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      button: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(13),
        minHeight: 30,
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.borderRadius.md,
        '&&': {
          color: neutral.P60
        },
        '& .MuiButton-startIcon': {
          marginRight: theme.spacing(0.75)
        },
        '&:hover': {
          backgroundColor: neutral.P96
        },
        '@media print': {
          display: 'none'
        }
      }
    }
  },
  { name: 'OptionsConsumerToggle' }
)

const CONSUMER_OPTIONS: Array<{ value: 'crew' | 'customer'; label: string }> = [
  { value: 'customer', label: 'Customer/Price' },
  { value: 'crew', label: 'Crew/Hours' }
]

/**
 * The Work Order Options card's display switcher — matches the legacy `WorkOrderView`'s
 * `AreaSubstrateView` control (a Visibility icon + label opening a menu of Customer/Price vs
 * Crew/Hours), ported onto the shared estimateNext Button/Menu pattern (see the nav bar's hours
 * dropdown in `WorkOrderNavBarNext`). Switches whether the Options card shows customer prices or
 * crew hours (`quote.options.workOrderOptionsConsumer`).
 */
export default function OptionsConsumerToggle({ value, onChange }: OptionsConsumerToggleProps) {
  const classes = useStyles()
  const { __ } = useTranslation()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const selected = CONSUMER_OPTIONS.find((option) => option.value === value) ?? CONSUMER_OPTIONS[0]

  return (
    <>
      <Button
        variant="text"
        size="small"
        className={classes.button}
        startIcon={<VisibilityIcon />}
        endIcon={anchor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={(ev: React.MouseEvent<HTMLElement>) => setAnchor(ev.currentTarget)}
      >
        {__(selected.label)}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {CONSUMER_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              setAnchor(null)
              onChange(option.value)
            }}
          >
            {__(option.label)}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
